import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  CustomDataBaseModel,
  FieldType,
  ObjectDefinitionModel,
} from '@seahorse/domain';
import {
  BadgeDirective,
  CopyContentButtonComponent,
  EllipsisDirective,
  FieldValuePipe,
} from '@seahorse/ui';
import { CustomDataFormService } from '../../form/custom-data-form.service';
import {
  FieldFormDirective,
  FieldFormValue,
} from '../../form/field-form.directive';
import { FormModel } from '../../form/form.model';
import { EditAttributeModal } from '../edit-attribute-modal/edit-attribute-modal.component';
import { FieldAttributeModel } from '../field-attribute.model';
import { FieldModeDirective } from '../field-mode.directive';
import { AppendTextPipe, TruncatePipe } from '@seahorse/common';

@Component({
  selector: 'temp-attribute-list',
  templateUrl: 'attribute-list.component.html',
  styleUrls: ['./attribute-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CopyContentButtonComponent,
    TranslateModule,
    BadgeDirective,
    FieldValuePipe,
    EllipsisDirective,
    FieldFormDirective,
    FieldModeDirective,
    TruncatePipe,
    AppendTextPipe,
  ],
})
export class AttributeListComponent implements OnInit {
  @Input() allowEdit = false;

  private _attributes: FieldAttributeModel[] = [];
  @Input() set attributes(val: FieldAttributeModel[]) {
    this.attributeGroups = val.reduce((acc, cur) => {
      const groupName = cur.groupName || '-1';
      acc[groupName] = acc[groupName] || [];
      acc[groupName].push(cur);

      cur.displayValue = this.formatDisplayValue(cur);

      return acc;
    }, {});

    this._attributes = val;

    if (this.isNewSetup) {
      this.setUpFormModels(val);
    }
  }

  @Input() columns = 1;

  @Input() displayField?: string;
  @Input() modalHeader: string;
  @Input() objectDefinition?: ObjectDefinitionModel;
  @Input() showGroupTitles = false;
  @Input() objectValue: CustomDataBaseModel;

  @Output() attributeChanged = new EventEmitter<FieldAttributeModel>();

  attributeGroups?: { [group: string]: FieldAttributeModel[] };
  formModels: { [key: string]: FormModel } = {};

  FieldType = FieldType;

  get isNewSetup() {
    return !!this.objectDefinition;
  }

  constructor(
    private modal: NgbModal,
    private translateService: TranslateService,
    private _customDataFormService: CustomDataFormService
  ) {}

  ngOnInit() {
    if (this.isNewSetup) {
      this.setUpFormModels(this._attributes);
    }
  }

  private setUpFormModels(attributes: FieldAttributeModel[]) {
    this.formModels = attributes.reduce((acc, cur) => {
      const model = this.getFieldModel(
        cur.fieldCode,
        cur.fieldValue,
        cur.fieldFormatting
      );
      acc[cur.fieldCode] = model;
      return acc;
    }, <{ [key: string]: FormModel }>{});
  }

  private getFieldModel(key: string, value: string, formatting?: any) {
    return this._customDataFormService.getFieldModel(
      this.objectDefinition,
      key,
      value,
      formatting
    );
  }

  submitForm(
    attr: FieldAttributeModel,
    { value: fieldValue, id }: { value: FieldFormValue; id: any }
  ) {
    if (id !== this.objectValue?.__Id) {
      return;
    }

    const value = fieldValue[attr.fieldCode];
    const formatting =
      value != null
        ? fieldValue.__DataFieldFormatting?.[attr.fieldCode]
        : undefined;

    attr.fieldValue = value;
    attr.fieldFormatting = formatting;

    this.attributeChanged.emit(attr);

    const model = this.getFieldModel(
      attr.fieldCode,
      attr.fieldValue,
      attr.fieldFormatting
    );
    this.formModels[attr.fieldCode] = model;
  }

  // old field edit
  editField(attribute: FieldAttributeModel) {
    if (!this.allowEdit) {
      return;
    }

    const attributeCopy: FieldAttributeModel = new FieldAttributeModel(
      attribute.fieldName,
      attribute.fieldType,
      attribute.fieldValue,
      attribute.fieldCode,
      attribute.isRequired,
      null,
      attribute.fieldFormatting,
      attribute.isUnique
    );
    const ngbModalOptions: NgbModalOptions = {
      backdrop: 'static',
      size: 'sm',
    };

    const modalRef = this.modal.open(EditAttributeModal, ngbModalOptions);
    modalRef.componentInstance.attribute = attributeCopy;
    modalRef.componentInstance.modalHeader = this.modalHeader
      ? this.translateService.instant(this.modalHeader)
      : null;

    modalRef.result
      .then((modalResult) => {
        if (modalResult) {
          // DON'T return the copy object but only parse the needed field to the original attribute.
          // Bacause the originel attribute could contain more field than FieldAttributeModel. e.g. the ship attribute contains 'Category'
          attribute.fieldValue = modalResult.fieldValue;
          attribute.fieldFormatting = modalResult.fieldFormatting;
          attribute.displayValue = this.formatDisplayValue(attribute);
          this.attributeChanged.emit(attribute);
        }
      })
      .catch((res) => {
        //
      });
  }

  getTaskProgressFraction(value) {
    const tasks = value.split('/').map(Number);

    if (
      tasks.length === 2 &&
      !isNaN(tasks[0]) &&
      !isNaN(tasks[1]) &&
      tasks[1] !== 0
    ) {
      const taskStatus = {
        Progress: tasks[0],
        Total: tasks[1],
      };

      if (taskStatus.Progress === 0) {
        return 1;
      }

      if (
        taskStatus.Progress === taskStatus.Total &&
        taskStatus.Progress / taskStatus.Total === 1
      ) {
        return 0;
      }

      return taskStatus.Progress / taskStatus.Total;
    }

    return 1;
  }

  clickLabel(event, id: string) {
    const dd = document.getElementById(id);
    if (dd) {
      dd.click();
    }
    event.stopPropagation();
  }

  formatDisplayValue(attribute: FieldAttributeModel): unknown {
    let value = attribute.displayValue;

    switch (attribute.fieldType) {
      case FieldType.LinkedObject:
        value = attribute.displayValue?.replace(/\n/g, ' | ');
        break;

      case FieldType.Currency:
        if (
          attribute.fieldValue !== undefined &&
          attribute.fieldValue !== null
        ) {
          value = new CurrencyPipe('nl-NL').transform(
            attribute.fieldValue,
            attribute.fieldFormatting?.['CurrencyCode'] || 'EUR',
            'symbol-narrow',
            '1.2-2'
          );
        }
        break;
    }

    return value;
  }
}
